/**
 * "Update Dates" — the customer setting the three dates OUTRIGHT while the PO
 * line is still `new`.
 *
 * EXPEDITING-PARITY PR 2, P10 in the parity matrix. Aaron, 17 Sep 2026: *"In
 * legacy when a line is in new status, the customer at the group level has the
 * ability to change the customer required date, the original required date, and
 * the supplier promise date. This is only when the line is in new status. Once
 * the supplier has acknowledged it they don't have that ability."*
 *
 * This is NOT ProposeChangeModal in another coat, and the copy works hard to
 * say so: nothing here is proposed, nobody accepts it, and the dates are on the
 * line the moment Update Dates is pressed. At `new` the supplier has answered
 * nothing, so there is no counterparty position to overwrite — which is exactly
 * why legacy allows it here and nowhere else (`OpenOrders.js:1961` gates on
 * `PO_AcknowledgeStatus == 'New' && companyType == 1`, routing to
 * `OpenOrdersController.cs:3778 ProposeChange4New`).
 *
 * Deliberately a separate component from ProposeChangeModal rather than a mode
 * inside it: that file was already close to helix-code's 500-line cap (and went
 * over it before this item split a shared panel out of both), and a modal that
 * sometimes negotiates and sometimes writes is the kind of double meaning this
 * whole item exists to remove.
 *
 * The server is the real gate (`POLineWorkflowService.set_dates_at_new` refuses
 * a non-customer caller, a line past `new`, and a held line). Every rule here is
 * cosmetic and has to agree with it, or the screen offers a button that 400s.
 */
import { useEffect, useState } from 'react';
import { X, CalendarCheck, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';
import { ReasonCodeSelect, getEffectiveReason } from './AcknowledgmentSharedUtils';
import { LineIdentityPanel } from './LineIdentityPanel';
import {
  CUSTOMER_REQUIRED_DATE_SHORT,
  ORIGINAL_REQUIRED_DATE_SHORT,
  SUPPLIER_PROMISE_DATE_SHORT,
} from '../../config/dateLabels';

export interface SetDatesPayload {
  customerRequiredDate?: string;
  originalRequiredDate?: string;
  supplierPromiseDate?: string;
  reason: string;
  comments?: string;
}

interface SetDatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineNumber: string | number;
  partNumber?: string;
  orderNumber?: string;
  /** Current values, as the payload spells them (delivery_date is the CRD). */
  currentCustomerRequiredDate?: string | null;
  currentOriginalRequiredDate?: string | null;
  currentSupplierPromiseDate?: string | null;
  onSubmit: (payload: SetDatesPayload) => Promise<void>;
}

/**
 * The picker's floor: today in UTC, which is the day the SERVER means.
 *
 * It must be the UTC day, not the browser's. `set_dates_at_new` compares
 * against `timezone.localdate()`, and Helix runs `TIME_ZONE = 'UTC'`
 * (`helix_backend/settings.py:294`) with no `timezone.activate` anywhere, so
 * the server's "today" is the UTC day for every user. Checked on the running
 * stack: TIME_ZONE UTC, localdate() and date.today() both 2026-09-20.
 *
 * A browser-local day was tried here first and was wrong. West of UTC the two
 * diverge from early evening: a New York user at 20:30 on the 19th would be
 * offered the 19th by their own clock while the server had already moved to
 * the 20th, so the picker would hand them a date the endpoint then refuses.
 * The UTC form agrees with the server everywhere, which is why
 * ProposeChangeModal:344, AcknowledgmentWorkflowModals.tsx:422 and schedule
 * lines all use it.
 */
const todayForPicker = (): string => new Date().toISOString().split('T')[0];

/** A payload date is `YYYY-MM-DD`; an input needs exactly that, or nothing. */
const toInputDate = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
};

export function SetDatesModal({
  isOpen,
  onClose,
  lineNumber,
  partNumber,
  orderNumber,
  currentCustomerRequiredDate,
  currentOriginalRequiredDate,
  currentSupplierPromiseDate,
  onSubmit,
}: SetDatesModalProps) {
  const [crd, setCrd] = useState('');
  const [odd, setOdd] = useState('');
  const [spd, setSpd] = useState('');
  const [reasonSelect, setReasonSelect] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Seed from the line every time it opens, so the boxes show what is on the
  // line now and an untouched box submits no change (the server skips a date
  // set to the value it already holds, so an accidental no-op writes nothing).
  useEffect(() => {
    if (!isOpen) return;
    setCrd(toInputDate(currentCustomerRequiredDate));
    setOdd(toInputDate(currentOriginalRequiredDate));
    setSpd(toInputDate(currentSupplierPromiseDate));
    setReasonSelect('');
    setCustomReason('');
    setComments('');
  }, [isOpen, currentCustomerRequiredDate, currentOriginalRequiredDate, currentSupplierPromiseDate]);

  const dirty = (value: string, original?: string | null) => value !== toInputDate(original);
  const changed = [
    dirty(crd, currentCustomerRequiredDate),
    dirty(odd, currentOriginalRequiredDate),
    dirty(spd, currentSupplierPromiseDate),
  ].filter(Boolean).length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (changed === 0) {
      toast.error('Change at least one date first');
      return;
    }
    // Clearing a date is not what this control is for — legacy writes dates, it
    // never blanks one — and an empty box reads as "leave it alone" on the wire,
    // so emptying a box that HAD a value would silently do nothing. That box is
    // refused.
    //
    // What it must NOT do is demand a date the line does not have (F5, gate
    // round 1). This rule used to be `!crd || !odd || !spd`, which refused EVERY
    // submission on a line with no supplier promise date — the normal state of a
    // `new` line, since the supplier has promised nothing yet — and the only way
    // through it was to invent a supplier commitment and have the server lock it
    // at AGREED. An empty box over a NULL field is now simply left alone.
    const cleared = [
      { label: CUSTOMER_REQUIRED_DATE_SHORT, value: crd, original: currentCustomerRequiredDate },
      { label: ORIGINAL_REQUIRED_DATE_SHORT, value: odd, original: currentOriginalRequiredDate },
      { label: SUPPLIER_PROMISE_DATE_SHORT, value: spd, original: currentSupplierPromiseDate },
    ].find((field) => !field.value && !!toInputDate(field.original));
    if (cleared) {
      toast.error(`${cleared.label} can be changed, but not cleared`);
      return;
    }
    // EXPEDITING-3 (Aaron, 30 Sep 2026): at New nothing is being asked of the
    // supplier, and the audit trail already records who changed what and when,
    // so the reason code and the comment are both optional.
    const reason = getEffectiveReason(reasonSelect, customReason);

    setSubmitting(true);
    try {
      await onSubmit({
        // Only what actually moved: an unchanged date is omitted, so it never
        // reaches the changelog and never takes a field lock it did not earn.
        ...(dirty(crd, currentCustomerRequiredDate) ? { customerRequiredDate: crd } : {}),
        ...(dirty(odd, currentOriginalRequiredDate) ? { originalRequiredDate: odd } : {}),
        ...(dirty(spd, currentSupplierPromiseDate) ? { supplierPromiseDate: spd } : {}),
        reason,
        comments,
      });
      onClose();
    } catch (error) {
      // The caller toasts the server's message; this only keeps the modal open
      // so the typed dates are not lost.
      console.error('Failed to update dates:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // The id ties the label to the input: a bare <label> beside a date box leaves
  // the box with NO accessible name, so a screen reader announces three
  // identical "date" fields and a test can only reach them by position (F13,
  // gate round 1).
  const dateField = (
    id: string,
    label: string,
    value: string,
    setValue: (next: string) => void,
    original?: string | null,
    hint?: string,
  ) => (
    <div>
      <label htmlFor={id} className="block text-sm font-medium mb-1">{label}</label>
      <input
        id={id}
        type="date"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        // CR-7428: the picker greys out every day before today, as it already
        // does on Propose Change (ProposeChangeModal:344), the acknowledgement
        // flow and schedule lines. The form carries noValidate, so this greys
        // the calendar WITHOUT a seeded past value blocking the submit.
        min={todayForPicker()}
        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
      />
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
      {dirty(value, original) && (
        <p className="text-xs text-primary mt-1">
          Changing from {toInputDate(original) || 'not set'}
        </p>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <CalendarCheck className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">Update Dates</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <CardContent className="p-4">
          {/* noValidate, deliberately, and it is load-bearing for CR-7428.
              Each box is SEEDED with the date the line holds, and a line can
              legitimately arrive from the ERP already overdue. With native
              validation live, `min` made those seeded values rangeUnderflow,
              so the browser refused to submit the form at all and the customer
              could not even move the promise date forward on an overdue line.
              Measured on the local stack against a line whose CRD and ODD were
              40 days past: form.checkValidity() false, zero requests sent, the
              dialog stuck open with a bubble on a field nobody had touched.

              `min` still greys the earlier days, which is the point. What is
              dropped is the browser blocking a value the user did not choose.
              The guard that counts is the server's, and this form posts only
              the fields that moved. The dialog's own checks (a date changed, a
              date not cleared) run in handleSubmit. */}
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <LineIdentityPanel
              lineNumber={lineNumber}
              partNumber={partNumber}
              orderNumber={orderNumber}
            />

            <p className="text-sm text-muted-foreground">
              These dates go straight onto the line — the supplier has not
              answered this order yet, so there is nothing to agree. Once they
              acknowledge it, changes go through Propose Change instead.
            </p>

            {dateField(
              'set-dates-crd', CUSTOMER_REQUIRED_DATE_SHORT, crd, setCrd, currentCustomerRequiredDate,
              'When you need it.',
            )}
            {dateField(
              'set-dates-odd', ORIGINAL_REQUIRED_DATE_SHORT, odd, setOdd, currentOriginalRequiredDate,
              // A line whose ODD is NULL is measured against its CRD, so moving
              // the CRD would move the measure too. The server pins it at the
              // date that stood before the edit (F3); this says so BEFORE the
              // press rather than leaving it to be discovered in the audit trail.
              // Keyed off the BOX, not the stored value: type an ODD and the pin
              // no longer fires (an explicit ODD wins), so the hint must stop
              // promising it.
              !odd && dirty(crd, currentCustomerRequiredDate)
                ? `Not set, so performance is measured against the ${CUSTOMER_REQUIRED_DATE_SHORT}. `
                  + `Moving that date pins this one at ${toInputDate(currentCustomerRequiredDate) || 'its current value'} `
                  + 'so the measure stays put. Type a date here to re-baseline it deliberately.'
                : 'The date delivery performance is measured against.',
            )}
            {dateField(
              'set-dates-spd', SUPPLIER_PROMISE_DATE_SHORT, spd, setSpd, currentSupplierPromiseDate,
              'What the supplier is committed to.',
            )}

            <ReasonCodeSelect
              value={reasonSelect}
              onChange={setReasonSelect}
              customValue={customReason}
              onCustomChange={setCustomReason}
              label="Reason Code (optional)"
              categories={['date_change']}
            />

            <div>
              <label className="block text-sm font-medium mb-2">
                Additional Comments
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
                placeholder="Provide additional details about the change..."
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 border rounded-lg hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || changed === 0}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <CalendarCheck className="h-4 w-4" />
                    Update {changed} Date{changed === 1 ? '' : 's'}
                  </>
                )}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
